/**
 * SitePromos.jsx — DB-driven banners + modals + toasts (iter 117).
 *
 * Renders pre-styled components whose copy comes from /api/site-promos/active.
 * Operators add a new promo via /admin/site-promos and it appears here without
 * a code change. All variants share one design system:
 *   - dark bg (matches BTK theme), gold accents, shimmer CTA on promo tone
 *   - dismissible (sessionStorage flag per slug)
 *   - never stacks on top of cookie/mailing-list modal (popupGate)
 *
 * Two ways to use:
 *   1. <SitePromos /> at app root — pulls page="*" + the current path
 *   2. <SitePromos page="funnels" /> — explicit per-page (overrides path)
 *
 * Render budget:
 *   - 1 banner at top + 1 banner at bottom max
 *   - 1 modal max (highest priority wins; inactivity & exit_intent listen
 *     for their respective signals — see ExitIntentPopup / MailingListPopup
 *     for the existing infrastructure they piggyback on)
 *   - N toasts (Sonner queues them naturally)
 *
 * The 5 placements:
 *   banner.top         — sticky bar above page content
 *   banner.bottom      — sticky bar at viewport bottom
 *   modal.on_load      — opens immediately on page mount (with priority filter)
 *   modal.timed        — opens after delay_seconds
 *   modal.exit_intent  — wired via the existing ExitIntentPopup signal hook
 *   modal.inactivity   — opens after 60s no input (browser idle)
 *   toast.on_load      — Sonner toast immediately on mount
 *   toast.timed        — Sonner toast after delay_seconds
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { isOtherPopupOpen } from "../../lib/popupGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_STORAGE_PREFIX = "btk_site_promo_dismissed_v1:";
const SHOWN_STORAGE_PREFIX = "btk_site_promo_shown_v1:";
const INACTIVITY_MS = 60 * 1000; // 60s of no input

// ---------------------------------------------------------------------------
// SessionStorage helpers — promo dismissals are session-scoped (refresh =
// they come back). Use localStorage manually for hard dismiss if needed.
// ---------------------------------------------------------------------------
function dismissed(slug) {
  try {
    return sessionStorage.getItem(DISMISS_STORAGE_PREFIX + slug) === "1";
  } catch {
    return false;
  }
}
function markDismissed(slug) {
  try {
    sessionStorage.setItem(DISMISS_STORAGE_PREFIX + slug, "1");
  } catch {
    /* noop */
  }
}
function alreadyShown(slug) {
  try {
    return sessionStorage.getItem(SHOWN_STORAGE_PREFIX + slug) === "1";
  } catch {
    return false;
  }
}
function markShown(slug) {
  try {
    sessionStorage.setItem(SHOWN_STORAGE_PREFIX + slug, "1");
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Tone palette — keeps the design system pre-made; operators only change copy.
// ---------------------------------------------------------------------------
const TONE_STYLES = {
  neutral: {
    bg: "bg-[#1A1D24]",
    border: "border-white/15",
    accent: "text-white",
    cta: "bg-white text-black hover:bg-white/90",
  },
  promo: {
    bg: "bg-gradient-to-r from-[#1a1208] via-[#1A1D24] to-[#1a1208]",
    border: "border-gold/30",
    accent: "text-gold",
    cta: "bk-shimmer text-black",
  },
  success: {
    bg: "bg-gradient-to-r from-emerald-950 via-[#1A1D24] to-emerald-950",
    border: "border-emerald-400/30",
    accent: "text-emerald-300",
    cta: "bg-emerald-400 text-black hover:bg-emerald-300",
  },
  warning: {
    bg: "bg-gradient-to-r from-amber-950 via-[#1A1D24] to-amber-950",
    border: "border-amber-400/30",
    accent: "text-amber-300",
    cta: "bg-amber-400 text-black hover:bg-amber-300",
  },
};
const toneFor = (t) => TONE_STYLES[t] || TONE_STYLES.neutral;

// ===========================================================================
// Banner component (sticky top / bottom)
// ===========================================================================
function PromoBanner({ promo, position, onDismiss }) {
  const palette = toneFor(promo.tone);
  const stickyClass = position === "top" ? "top-0" : "bottom-0";
  return (
    <div
      data-testid={`site-promo-banner-${promo.slug}`}
      className={`fixed left-0 right-0 ${stickyClass} z-40 ${palette.bg} ${palette.border} border-y backdrop-blur-md`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
        {promo.eyebrow && (
          <span className={`hidden md:inline-flex shrink-0 text-[10px] uppercase tracking-[0.22em] font-bold ${palette.accent}`}>
            {promo.eyebrow}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm md:text-base font-bold leading-tight truncate md:whitespace-normal">
            {promo.headline}
          </div>
          {promo.body && (
            <div className="text-white/70 text-xs md:text-sm mt-0.5 line-clamp-2 md:line-clamp-1">
              {promo.body}
            </div>
          )}
        </div>
        {promo.cta_label && promo.cta_url && (
          promo.cta_url.startsWith("/") ? (
            <Link
              to={promo.cta_url}
              data-testid={`site-promo-banner-cta-${promo.slug}`}
              className={`shrink-0 px-4 py-1.5 rounded-full text-[10px] md:text-xs uppercase tracking-[0.22em] font-bold ${palette.cta} transition-colors`}
            >
              {promo.cta_label}
            </Link>
          ) : (
            <a
              href={promo.cta_url}
              data-testid={`site-promo-banner-cta-${promo.slug}`}
              className={`shrink-0 px-4 py-1.5 rounded-full text-[10px] md:text-xs uppercase tracking-[0.22em] font-bold ${palette.cta} transition-colors`}
            >
              {promo.cta_label}
            </a>
          )
        )}
        {promo.dismissible !== false && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid={`site-promo-banner-dismiss-${promo.slug}`}
            aria-label="Dismiss"
            className="shrink-0 w-7 h-7 rounded-full border border-white/15 text-white/55 hover:text-white hover:border-white/30 transition-colors flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Modal component (overlay with backdrop)
// ===========================================================================
function PromoModal({ promo, onClose }) {
  const palette = toneFor(promo.tone);
  // Esc to close (only if dismissible)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && promo.dismissible !== false) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [promo.dismissible, onClose]);

  return (
    <div
      data-testid={`site-promo-modal-${promo.slug}`}
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-10"
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={promo.dismissible !== false ? onClose : undefined}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm cursor-pointer"
      />
      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-lg rounded-2xl ${palette.bg} ${palette.border} border p-6 md:p-8 max-h-[90vh] overflow-y-auto`}
        style={{ boxShadow: "0 30px 70px rgba(0,0,0,0.6), 0 0 36px rgba(63, 110, 233,0.10)" }}
      >
        {promo.dismissible !== false && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={`site-promo-modal-close-${promo.slug}`}
            className="absolute top-3 right-3 w-10 h-10 rounded-full border border-white/15 hover:border-white/55 text-white/75 hover:text-white transition-colors flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        )}

        {promo.eyebrow && (
          <span className={`text-[11px] uppercase tracking-[0.28em] ${palette.accent}`}>
            {promo.eyebrow}
          </span>
        )}
        <h3 className="mt-2 font-display text-2xl md:text-3xl tracking-tight leading-tight text-white">
          {promo.headline}
        </h3>
        {promo.body && (
          <p className="mt-3 text-sm md:text-base text-white/70 leading-relaxed">
            {promo.body}
          </p>
        )}

        {promo.cta_label && promo.cta_url && (
          <div className="mt-6">
            {promo.cta_url.startsWith("/") ? (
              <Link
                to={promo.cta_url}
                onClick={onClose}
                data-testid={`site-promo-modal-cta-${promo.slug}`}
                className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full ${palette.cta} text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform`}
              >
                {promo.cta_label}
              </Link>
            ) : (
              <a
                href={promo.cta_url}
                onClick={onClose}
                data-testid={`site-promo-modal-cta-${promo.slug}`}
                className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full ${palette.cta} text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform`}
              >
                {promo.cta_label}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Main controller — orchestrates fetch + dispatch per placement
// ===========================================================================
export default function SitePromos({ page: pageOverride = null }) {
  const location = useLocation();
  // The page slug we ask the server about. The server uses "*" + targeting
  // rules — so passing the current path lets it filter per-page automatically.
  const page = useMemo(() => {
    if (pageOverride) return pageOverride;
    const p = location.pathname.replace(/^\/+|\/+$/g, "");
    return p || "home";
  }, [pageOverride, location.pathname]);

  const [promos, setPromos] = useState([]);
  const [modalToShow, setModalToShow] = useState(null); // active modal promo doc
  const [topBanner, setTopBanner] = useState(null);
  const [bottomBanner, setBottomBanner] = useState(null);
  const inactivityTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);

  // -------------------------------------------------------------------------
  // Fetch on page change
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    fetchAbortRef.current = ctrl;

    const fetchPromos = async () => {
      try {
        const res = await fetch(`${API}/site-promos/active?page=${encodeURIComponent(page)}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) {
          if (!cancelled) setPromos([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setPromos(Array.isArray(data?.items) ? data.items : []);
      } catch (_) {
        if (!cancelled) setPromos([]);
      }
    };
    fetchPromos();
    return () => {
      cancelled = true;
      try { ctrl.abort(); } catch {}
    };
  }, [page]);

  // -------------------------------------------------------------------------
  // Dispatch — pick which promo goes where on every fetch
  // -------------------------------------------------------------------------
  const showModal = useCallback((promo) => {
    if (isOtherPopupOpen("site-promo-modal")) return; // never stack
    if (dismissed(promo.slug)) return;
    markShown(promo.slug);
    setModalToShow(promo);
  }, []);

  const showToast = useCallback((promo) => {
    if (dismissed(promo.slug)) return;
    if (alreadyShown(promo.slug)) return;
    markShown(promo.slug);
    const palette = toneFor(promo.tone);
    const opts = {
      duration: 8000,
      // Sonner picks up className internally
      className: `${palette.bg} ${palette.border} border text-white`,
    };
    if (promo.cta_label && promo.cta_url) {
      opts.action = {
        label: promo.cta_label,
        onClick: () => {
          if (promo.cta_url.startsWith("/")) {
            window.location.href = promo.cta_url;
          } else {
            window.open(promo.cta_url, "_blank", "noopener,noreferrer");
          }
        },
      };
    }
    toast(promo.headline, { description: promo.body || undefined, ...opts });
  }, []);

  useEffect(() => {
    if (!promos.length) {
      setTopBanner(null);
      setBottomBanner(null);
      return undefined;
    }

    // Banners: pick first non-dismissed of each position
    const topPromo = promos.find((p) => p.kind === "banner" && p.placement === "top" && !dismissed(p.slug)) || null;
    const bottomPromo = promos.find((p) => p.kind === "banner" && p.placement === "bottom" && !dismissed(p.slug)) || null;
    setTopBanner(topPromo);
    setBottomBanner(bottomPromo);

    const cleanups = [];

    // on_load modals — show immediately (first non-dismissed wins)
    const onLoadModal = promos.find((p) => p.kind === "modal" && p.placement === "on_load" && !dismissed(p.slug) && !alreadyShown(p.slug));
    if (onLoadModal) {
      // Defer one tick to let other popups (cookie banner) mount first
      const id = setTimeout(() => showModal(onLoadModal), 200);
      cleanups.push(() => clearTimeout(id));
    }

    // timed modals
    promos.filter((p) => p.kind === "modal" && p.placement === "timed" && !dismissed(p.slug) && !alreadyShown(p.slug))
      .forEach((p) => {
        const delay = (p.delay_seconds ?? 15) * 1000;
        const id = setTimeout(() => showModal(p), delay);
        cleanups.push(() => clearTimeout(id));
      });

    // inactivity modals — fire after INACTIVITY_MS of no user input
    const inactivityPromo = promos.find((p) => p.kind === "modal" && p.placement === "inactivity" && !dismissed(p.slug) && !alreadyShown(p.slug));
    if (inactivityPromo) {
      const resetInactivity = () => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => showModal(inactivityPromo), INACTIVITY_MS);
      };
      const events = ["mousemove", "keydown", "touchstart", "scroll"];
      events.forEach((e) => document.addEventListener(e, resetInactivity, { passive: true }));
      resetInactivity();
      cleanups.push(() => {
        events.forEach((e) => document.removeEventListener(e, resetInactivity));
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      });
    }

    // exit_intent modals — re-use the safe signals already wired in the page
    // (mouse-leave-top, visibility-hidden, scroll-up-after-deep). We attach
    // light listeners here so an admin-configured exit-intent promo doesn't
    // need to know about ExitIntentPopup props.
    const exitIntentPromo = promos.find((p) => p.kind === "modal" && p.placement === "exit_intent" && !dismissed(p.slug) && !alreadyShown(p.slug));
    if (exitIntentPromo) {
      let armed = false;
      const armTimer = setTimeout(() => { armed = true; }, 4000);
      const onMouseLeave = (e) => {
        // Suppress while the mega menu is open (see MegaMenu.jsx comment).
        if (document.body.dataset.megaMenuOpen === "true") return;
        if (armed && e.clientY <= 0) showModal(exitIntentPromo);
      };
      const onVisibility = () => {
        if (document.body.dataset.megaMenuOpen === "true") return;
        if (armed && document.visibilityState === "hidden") showModal(exitIntentPromo);
      };
      document.addEventListener("mouseleave", onMouseLeave);
      document.addEventListener("visibilitychange", onVisibility);
      cleanups.push(() => {
        clearTimeout(armTimer);
        document.removeEventListener("mouseleave", onMouseLeave);
        document.removeEventListener("visibilitychange", onVisibility);
      });
    }

    // toasts (on_load + timed)
    promos.filter((p) => p.kind === "toast" && p.placement === "on_load" && !dismissed(p.slug) && !alreadyShown(p.slug))
      .forEach((p) => {
        const id = setTimeout(() => showToast(p), 600);
        cleanups.push(() => clearTimeout(id));
      });
    promos.filter((p) => p.kind === "toast" && p.placement === "timed" && !dismissed(p.slug) && !alreadyShown(p.slug))
      .forEach((p) => {
        const delay = (p.delay_seconds ?? 15) * 1000;
        const id = setTimeout(() => showToast(p), delay);
        cleanups.push(() => clearTimeout(id));
      });

    return () => cleanups.forEach((fn) => fn());
  }, [promos, showModal, showToast]);

  // Close handlers
  const closeTopBanner = () => {
    if (topBanner) markDismissed(topBanner.slug);
    setTopBanner(null);
  };
  const closeBottomBanner = () => {
    if (bottomBanner) markDismissed(bottomBanner.slug);
    setBottomBanner(null);
  };
  const closeModal = () => {
    if (modalToShow) markDismissed(modalToShow.slug);
    setModalToShow(null);
  };

  return (
    <>
      {/* Sonner Toaster — mounted once for toast-kind promos. Multiple
          mounts coexist safely (Sonner dedupes). Skip if a page already
          mounts its own Toaster (most admin pages do). */}
      <Toaster position="bottom-right" theme="dark" richColors closeButton />
      {topBanner && (
        <PromoBanner promo={topBanner} position="top" onDismiss={closeTopBanner} />
      )}
      {bottomBanner && (
        <PromoBanner promo={bottomBanner} position="bottom" onDismiss={closeBottomBanner} />
      )}
      {modalToShow && <PromoModal promo={modalToShow} onClose={closeModal} />}
    </>
  );
}
